import moment from 'moment'
import { RoomOwner, RoomTitle, RoomProperties } from '@/infrastructure/network/Monitaro/schema/models'
import TalkerEntity, { ITalkerProps } from '@/entities/Talker'
import { IDoctorProps } from '@/entities/Doctor'

export interface IRoomProps {
  id?: string
  unreadCount: number
  users?: ITalkerProps[]
  doctors?: ITalkerProps[]
  parents?: ITalkerProps[]
  doctorId: string | null
  ownerId: string | null
  ownerType: string | null
  createdAt: string | null
  updatedAt: string | null
  latestAt: string | null
  owner?: RoomOwner
  title: string | null
  userTitle: RoomTitle | null
  enabled: boolean | null
  properties?: RoomProperties | null
  doctor: string | null
  room: string | null
  isLiteOnly?: boolean
}

export default class RoomEntity {
  private _props: IRoomProps

  constructor(props?: IRoomProps | null) {
    this._props = EmptyRoomPropsFactory(props)
  }

  get props(): IRoomProps {
    return this._props
  }

  get clone(): IRoomProps {
    return structuredClone(this._props)
  }

  get id(): string | undefined {
    return this._props.id
  }

  get unreadCount(): number | undefined {
    return this._props.unreadCount
  }

  get users(): TalkerEntity[] {
    return this._props.users ? this._props.users.map((prop) => new TalkerEntity(prop)) : []
  }

  get doctors(): TalkerEntity[] {
    return this._props.doctors ? this._props.doctors.map((prop) => new TalkerEntity(prop)) : []
  }

  get parents(): TalkerEntity[] {
    return this._props.parents ? this._props.parents.map((prop) => new TalkerEntity(prop)) : []
  }

  get ownerId(): string | null {
    return this._props.ownerId
  }

  get ownerType(): string | null {
    return this._props.ownerType
  }

  get createdAt(): string | null {
    return moment(this._props.createdAt).format('YYYY/M/D H:mm')
  }

  get updatedAt(): string | null {
    return moment(this._props.updatedAt).format('YYYY/M/D H:mm')
  }

  get latestAt(): string | null {
    if (this._props.latestAt) {
      const today = moment().startOf('day')
      const latest = moment(this._props.latestAt)

      if (latest.isSame(today, 'day')) {
        return latest.format('H:mm')
      } else if (latest.isSame(today, 'year')) {
        return latest.format('M/D')
      }
      return latest.format('YYYY/M/D')
    }
    return null
  }

  get owner(): RoomOwner | undefined {
    return this._props.owner
  }

  get title(): RoomTitle | null | undefined {
    return this._props.userTitle
  }

  get enabled(): boolean | null {
    return this._props.enabled
  }

  get properties(): RoomProperties | null | undefined {
    return this._props.properties
  }

  get publish(): boolean {
    return this._props.properties?.publish || false
  }

  get group(): boolean {
    return this._props.properties?.group || false
  }

  get isLiteOnly(): boolean {
    return this._props.isLiteOnly || false
  }

  get icon(): string | null {
    return this._props.userTitle?.icon || null
  }

  get initial(): string {
    return this.enabled ? this.talkers[0]?.fullname?.charAt(0) || '' : ''
  }

  get color(): string {
    if (this.enabled) {
      const code = this.talkers[0]?.fullname?.charCodeAt(0)
      if (code) {
        return colors[code % colors.length]
      }
    }
    return 'grey'
  }

  get icons(): TalkerEntity[] {
    return this.doctors
      .concat(this.parents)
      .concat(this.users)
      .filter((talker) => talker?.id !== this.talkers[0]?.id)
      .slice(0, 3)
  }

  get talkers(): ITalkerProps[] {
    return (this._props.doctors || []).concat(this._props.users || [])
  }
}

export const headers = [{ text: 'ID', value: 'id' }]
export const colors = ['#1976D2', '#388E3C', '#F57C00', '#7B1FA2', '#C2185B', '#00796B']

export const filters = [
  { text: 'すべて', value: 'all' },
  { text: '個人', value: 'individual' },
  { text: 'グループ', value: 'group' }
]

export const filters2 = [
  { text: 'すべて', value: 'all' },
  { text: '個人', value: 'doctor' },
  { text: 'グループ', value: 'room' }
]

// 新規メッセージの宛先候補(病院スタッフ・グループのどちらか一方を持つ)
export interface IRoomRecipientProps {
  id: string
  type: 'doctor' | 'group'
  doctor: IDoctorProps | null
  room: IRoomProps | null
}

export const EmptyRoomPropsFactory = (props?: Partial<IRoomProps> | null): IRoomProps => ({
  unreadCount: 0,
  doctorId: null,
  ownerId: null,
  ownerType: null,
  createdAt: null,
  updatedAt: null,
  latestAt: null,
  title: null,
  userTitle: {
    title: null,
    subtitle: null,
    icon: null
  },
  enabled: false,
  doctor: null,
  room: null,
  isLiteOnly: false,
  ...props,
  properties: EmptyRoomPropertiesFactory(props?.properties)
})

export const EmptyRoomPropertiesFactory = (props?: Partial<RoomProperties> | null): RoomProperties => ({
  group: false,
  publish: false,
  ...props
})
